import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useLottie, type LottieOptions } from 'lottie-react';
import { brandManageSlimGradient } from '../../../assets/animations';
import { isValidLottieAnimationData, normalizeLottieAnimationData } from '../../../utils/lottie';
import { ROUTES } from '../../../types/global/routes.types';

/** Matches the edge-to-edge Lottie composition (1488×226) and Social Manager content width. */
const LOTTIE_ASPECT = '1488 / 226';

function usePrefersReducedMotion(): boolean {
    const [reduced, setReduced] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false,
    );

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setReduced(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    return reduced;
}

function SlimGradientLottie({
    animationData,
    prefersReducedMotion,
}: {
    animationData: NonNullable<LottieOptions['animationData']>;
    prefersReducedMotion: boolean;
}) {
    const { View, goToAndStop } = useLottie(
        {
            animationData,
            loop: !prefersReducedMotion,
            autoplay: !prefersReducedMotion,
        },
        {
            width: '100%',
            height: '100%',
        },
    );

    useEffect(() => {
        if (!prefersReducedMotion) return;
        goToAndStop(0, true);
    }, [goToAndStop, prefersReducedMotion]);

    return <>{View}</>;
}

/**
 * Slim gradient promo for Brand Manage home. Width tracks the page content column;
 * baked CTA is covered by an accessible link to Team.
 */
export function BrandManagePromoBanner() {
    const prefersReducedMotion = usePrefersReducedMotion();

    if (!isValidLottieAnimationData(brandManageSlimGradient)) return null;
    const animationData = normalizeLottieAnimationData(brandManageSlimGradient) as NonNullable<
        LottieOptions['animationData']
    >;

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: LOTTIE_ASPECT,
                mb: 3.5,
                borderRadius: '16px',
                overflow: 'hidden',
                lineHeight: 0,
                bgcolor: 'transparent',
                '& svg': { display: 'block', width: '100% !important', height: '100% !important' },
            }}>
            <SlimGradientLottie animationData={animationData} prefersReducedMotion={prefersReducedMotion} />
            {/* Text/CTA are baked into the Lottie — overlay a real hit target. */}
            <Box
                component={RouterLink}
                to={ROUTES.BRAND_MANAGE_TEAM}
                aria-label="Build your team"
                sx={{
                    position: 'absolute',
                    left: { xs: '3%', sm: '4%' },
                    bottom: { xs: '14%', sm: '18%' },
                    width: { xs: '42%', sm: '26%', md: '22%' },
                    height: { xs: '28%', sm: '26%' },
                    borderRadius: 999,
                    zIndex: 1,
                    textDecoration: 'none',
                    '&:focus-visible': {
                        outline: '2px solid #131517',
                        outlineOffset: 3,
                    },
                }}
            />
        </Box>
    );
}
